import cloneDeep from "lodash/cloneDeep";
import * as z from "zod/v4";
import { discriminatedUnion, literal } from "zod/v4";
import { EmailInputMessage } from "../concierge-conversation/rpc/message";
import { ContentType, ConversationMessageType } from "../conversation";
import { BaseMessageDbParser } from "./base-conversation-message-db-parser";
import {
  BaseActionableContentMessageSchema,
  EmailInputMessageDbParser,
  HasComponentState,
  HasCustomerEmail,
  WithoutId,
} from "./message-parser-utils";
import {
  AddressInformationCustomerInputContent,
  AddressInformationCustomerInputContentSchema,
  AddressInformationMessageDbParser,
  AddressInformationMessageType,
} from "./message-parsers/address-information-message-parser";
import { BackInStockNotificationMessageDbParser } from "./message-parsers/back-in-stock-notification-message-parser";
import { CancelOrderMessageDbParser } from "./message-parsers/cancel-order-message-parser";
import { CartMessageDbParser } from "./message-parsers/cart-message-parser";
import { CheckOrderTrackingInfoDbParser } from "./message-parsers/check-order-tracking-info-message-parser";
import { ConfirmToolCallMessageDbParser } from "./message-parsers/confirm-tool-call-message-parser";
import { ContactSupportMessageDbParser } from "./message-parsers/contact-support-message-parser";
import { CreateBundleMessageDbParser } from "./message-parsers/create-bundle-message-parser";
import { DiscountCodeMessageDbParser } from "./message-parsers/discount-bubble-message-parser";
import { DisplayOrderTrackingInfoMessageDbParser } from "./message-parsers/display-order-tracking-info-message-parser";
import { FaqLinkMessageDbParser } from "./message-parsers/faq-link-message-parser";
import { ProductComparisonMessageDbParser } from "./message-parsers/product-comparison-message-parser";
import { ProductDetailsMessageDbParser } from "./message-parsers/product-details-message-parser";
import { ProductDisplayMessageDbParser } from "./message-parsers/product-display-message-parser";
import { ProductVisualizationResultMessageDbParser } from "./message-parsers/product-visualization-result-message-parser";
import {
  ProductVisualizationCustomerInputContent,
  ProductVisualizationCustomerInputContentSchema,
  ProductVisualizationUploadMessageDbParser,
  ProductVisualizationUploadMessageType,
} from "./message-parsers/product-visualization-upload-message-parser";
import { RelevantProductsMessageDbParser } from "./message-parsers/relevant-products-message-parser";
import {
  ResendReturnLabelCustomerInputContent,
  ResendReturnLabelCustomerInputContentSchema,
  ResendReturnLabelMessageDbParser,
  ResendReturnLabelMessageType,
} from "./message-parsers/resend-return-label-message-parser";
import { SubscribeToBackInStockMessageDbParser } from "./message-parsers/subscribe-to-back-in-stock-message-parser";
import { TextMessageDbParser } from "./message-parsers/text-message-parser";
import {
  ToolCallErrorMessageDbParser,
  ToolCallMessageDbParser,
} from "./message-parsers/tool-call-message-db-parser";
import {
  TrackOrderCustomerInputContent,
  TrackOrderCustomerInputContentSchema,
  TrackOrderUiMessageDbParser,
  TrackOrderUiMessageType,
} from "./message-parsers/track-order-message-parser";
import { UpdateOrderMessageDbParser } from "./message-parsers/update-order-message-parser";
import { WishlistMessageDbParser } from "./message-parsers/wishlist-message-parser";

const toolCallIdRefinement = (data: z.infer<typeof BaseMessageDbParser>) => {
  return !(data.type === ConversationMessageType.TOOL && !data.toolCallId);
};

const toolCallIdRefinementError = {
  message: "toolCallId is required for messages with type TOOL",
  path: ["toolCallId"],
};

export const ConversationMessageDbParserUnion = [
  TextMessageDbParser,
  ConfirmToolCallMessageDbParser,
  ProductDetailsMessageDbParser,
  BackInStockNotificationMessageDbParser,
  EmailInputMessageDbParser,
  RelevantProductsMessageDbParser,
  CheckOrderTrackingInfoDbParser,
  CartMessageDbParser,
  SubscribeToBackInStockMessageDbParser,
  ContactSupportMessageDbParser,
  ResendReturnLabelMessageDbParser,
  AddressInformationMessageDbParser,
  DiscountCodeMessageDbParser,
  ProductDisplayMessageDbParser,
  ProductComparisonMessageDbParser,
  ProductVisualizationUploadMessageDbParser,
  ProductVisualizationResultMessageDbParser,
  DisplayOrderTrackingInfoMessageDbParser,
  TrackOrderUiMessageDbParser,
  UpdateOrderMessageDbParser,
  CreateBundleMessageDbParser,
  FaqLinkMessageDbParser,
  ToolCallMessageDbParser,
  ToolCallErrorMessageDbParser,
  CancelOrderMessageDbParser,
  WishlistMessageDbParser,
] as const;

// Parser for new messages (without auto-generated fields)
export const ConversationMessageDbParser = z.preprocess(
  (data: any) => {
    // Create a deep copy to avoid mutating the original data
    const processedData = cloneDeep(data);

    // Transform date fields from string to Date if needed
    const dateFields = [
      "sentAt",
      "createdAt",
      "updatedAt",
      "aiMerchantFeedbackUpdatedAt",
    ];

    for (const field of dateFields) {
      if (processedData[field] && typeof processedData[field] === "string") {
        processedData[field] = new Date(processedData[field]);
      }
    }

    // Handle nested date fields in aiGenerations
    if (
      processedData.aiGenerations &&
      Array.isArray(processedData.aiGenerations)
    ) {
      for (const generation of processedData.aiGenerations) {
        if (generation.createdAt && typeof generation.createdAt === "string") {
          generation.createdAt = new Date(generation.createdAt);
        }
        if (generation.updatedAt && typeof generation.updatedAt === "string") {
          generation.updatedAt = new Date(generation.updatedAt);
        }
      }
    }

    // Handle nested date fields in files
    if (processedData.files && Array.isArray(processedData.files)) {
      for (const file of processedData.files) {
        if (file.createdAt && typeof file.createdAt === "string") {
          file.createdAt = new Date(file.createdAt);
        }
        if (file.updatedAt && typeof file.updatedAt === "string") {
          file.updatedAt = new Date(file.updatedAt);
        }
      }
    }

    // Set default contentType if not provided
    if (!processedData.contentType) {
      processedData.contentType = ContentType.TEXT;
    }

    return processedData;
  },
  discriminatedUnion("contentType", [
    ...ConversationMessageDbParserUnion,
  ]).refine(toolCallIdRefinement, toolCallIdRefinementError),
);

export type ActionableContentMessage = z.infer<
  typeof BaseActionableContentMessageSchema
>;
export type ConversationMessage = z.infer<typeof ConversationMessageDbParser>;
export type ConversationMessageWithoutId = WithoutId<ConversationMessage>;

export function isEmailInputMessage(
  message: ActionableContentMessage,
): message is ActionableContentMessage & EmailInputMessage {
  return message.contentType === ContentType.EMAIL_INPUT;
}

export type CustomerInputMessageContentType =
  | ContentType.RESEND_RETURN_LABEL_UI
  | ContentType.TRACK_ORDER_UI
  | ContentType.UPDATE_ADDRESS
  | ContentType.PRODUCT_VISUALIZATION_UPLOAD_UI;

type CustomerInputContent =
  | ResendReturnLabelCustomerInputContent
  | TrackOrderCustomerInputContent
  | AddressInformationCustomerInputContent
  | ProductVisualizationCustomerInputContent;
type CustomerInputMessage =
  | ResendReturnLabelMessageType
  | TrackOrderUiMessageType
  | AddressInformationMessageType
  | ProductVisualizationUploadMessageType;

export const CustomerInputMessageContentTypeToCustomerInputMessageMap: Record<
  CustomerInputMessageContentType,
  z.ZodType<CustomerInputMessage>
> = {
  [ContentType.RESEND_RETURN_LABEL_UI]: ResendReturnLabelMessageDbParser,
  [ContentType.TRACK_ORDER_UI]: TrackOrderUiMessageDbParser,
  [ContentType.UPDATE_ADDRESS]: AddressInformationMessageDbParser,
  [ContentType.PRODUCT_VISUALIZATION_UPLOAD_UI]:
    ProductVisualizationUploadMessageDbParser,
};

export const CustomerInputMessageContentTypeToCustomerInputContentMap: Record<
  CustomerInputMessageContentType,
  z.ZodType<CustomerInputContent>
> = {
  [ContentType.RESEND_RETURN_LABEL_UI]:
    ResendReturnLabelCustomerInputContentSchema,
  [ContentType.TRACK_ORDER_UI]: TrackOrderCustomerInputContentSchema,
  [ContentType.UPDATE_ADDRESS]: AddressInformationCustomerInputContentSchema,
  [ContentType.PRODUCT_VISUALIZATION_UPLOAD_UI]:
    ProductVisualizationCustomerInputContentSchema,
};

export const CustomerMessageDbParser = BaseMessageDbParser.extend({
  type: literal(ConversationMessageType.CUSTOMER),
});

export const MerchantMessageDbParser = BaseMessageDbParser.extend({
  type: literal(ConversationMessageType.MERCHANT),
});

// Type that automatically includes all conversation messages with component state
export type ComponentStateMessage = HasComponentState<ConversationMessage>;

// Helper to check if a message has component state at runtime
export function hasComponentState(
  message: ConversationMessage,
): message is ComponentStateMessage {
  return "confirmationStateInformation" in message;
}

// Type that automatically includes all conversation messages with customerEmail
export type CustomerEmailMessage = HasCustomerEmail<ConversationMessage>;

// Helper to check if a message has customerEmail at runtime
export function hasCustomerEmail(
  message: ConversationMessage,
): message is CustomerEmailMessage {
  return (
    !!message.structuredContent &&
    typeof message.structuredContent === "object" &&
    "customerEmail" in message.structuredContent
  );
}
